//version2
import React, { useState, useRef, useEffect } from 'react';
import { Search, Send, Users, Database, Clock, BarChart2, CheckCircle, Calendar, Globe, CreditCard, Zap, Camera, Link } from 'lucide-react';

const SwipableFeatureCardsSection = () => {
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const cardsContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  
  // Updated features data with new value props
  const features = [
    { 
      id: 'WEBSITE', 
      icon: <Globe />,
      title: 'WEBSITE',
      subtitle: 'SEO-Optimized Landing Page',
      description: 'Show up on Google, tell your story, and convert visitors into clients — all without code.',
      features: [
        'Professional landing pages in minutes',
        'Built-in SEO optimization',
        'Integrated blog for content marketing',
        'Mobile-responsive design'
      ]
    },
    { 
      id: 'BOOKINGS', 
      icon: <Calendar />, 
      title: 'BOOKINGS',
      subtitle: 'Payments & Packages Built In',
      description: 'Clients can book sessions, buy packages, and pay — all in one place, 24/7.',
      features: [
        'Automated scheduling system',
        'Package deals and subscriptions',
        'Secure payment processing',
        'No-show protection built in'
      ]
    },
    { 
      id: 'LEADS', 
      icon: <Zap />, 
      title: 'LEADS',
      subtitle: 'Smart Lead Generation',
      description: 'Capture leads automatically with forms, popups, and follow-up flows.',
      features: [
        'Smart popup forms',
        'Email capture sequences',
        'Automated follow-up workflows',
        'Lead scoring and qualification'
      ]
    },
    { 
      id: 'CONTENT', 
      icon: <Camera />, 
      title: 'CONTENT',
      subtitle: 'AI-Powered Social Media',
      description: 'Get auto-generated posts, carousels, and captions for Instagram, LinkedIn, etc.',
      features: [
        'AI-generated social posts',
        'Custom carousel designs',
        'Platform-optimized content',
        'Content calendar automation'
      ]
    },
    { 
      id: 'PORTAL', 
      icon: <Link />, 
      title: 'PORTAL',
      subtitle: 'Link-in-Bio & Client Hub',
      description: 'One link for all your offers, updates, content, and a private space for your clients.',
      features: [
        'Unified link-in-bio page',
        'Private client portal',
        'Resource sharing hub',
        'Progress tracking dashboard'
      ]
    }
  ];
  
  // Function to determine which card is most visible
  const updateActiveCard = () => {
    if (cardsContainerRef.current) {
      const container = cardsContainerRef.current;
      const scrollPosition = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      
      // Check if we're at the right edge of the scroll container
      const maxScroll = container.scrollWidth - containerWidth;
      const isAtEnd = scrollPosition >= maxScroll - 20;
      
      if (isAtEnd && !isScrolling) {
        // Scroll back to the first card when reaching the end
        setIsScrolling(true);
        container.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
        setActiveCardIndex(0);
        setTimeout(() => setIsScrolling(false), 500);
        return;
      }
      
      // Find which card is most visible
      const cards = Array.from(container.querySelectorAll('.feature-card'));
      let closestCard = 0;
      let closestDistance = Infinity;
      
      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
        const containerCenter = scrollPosition + (containerWidth / 2);
        const distance = Math.abs(cardCenter - containerCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestCard = index;
        }
      });
      
      if (closestCard !== activeCardIndex) {
        setActiveCardIndex(closestCard % features.length);
      }
    }
  };
  
  // Create "infinite" scroll effect by duplicating cards
  const extendedFeatures = [...features, ...features.slice(0, 2)];
  
  // Handle scroll events
  useEffect(() => {
    const container = cardsContainerRef.current;
    let scrollTimer;
    
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        updateActiveCard();
      }, 100);
    };
    
    if (container) {
      container.addEventListener('scroll', handleScroll);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimer);
      };
    }
  }, [activeCardIndex]);
  
  // Track horizontal movement to distinguish between scrolling and swiping
  const [touchStartY, setTouchStartY] = useState(0);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  
  // Scroll to a specific card test
  const scrollToCard = (index) => {
    if (cardsContainerRef.current) {
      const cardElements = cardsContainerRef.current.querySelectorAll('.feature-card');
      if (cardElements && cardElements[index]) {
        cardsContainerRef.current.scrollTo({
          left: cardElements[index].offsetLeft - 16, // Account for container padding
          behavior: 'smooth'
        });
      }
    }
  };
  
  return (
    <section id="features" className="py-4 md:py-16 bg-gradient-to-br from-gray-50 to-[#c4ffa0]/10">
      {/* Container with no horizontal padding on mobile */}
      <div className="max-w-6xl mx-auto px-0 md:px-6">
        <div className="text-center mb-6 md:mb-10 px-4">
          {/* <div className="inline-flex items-center mb-2 bg-[#c4ffa0]/20 px-3 py-1 rounded-full text-xs md:text-sm">
            <span className="mr-1 md:mr-2">✨</span>
            <span className="text-[#2d4a1e] font-medium">ALL-IN-ONE MARKETING</span>
            <span className="ml-1 md:ml-2">✨</span>
          </div> */}
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">The AI Growth System For Service Experts</h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
            Stop juggling multiple tools. Get everything you need to attract, convert, and retain clients in one beautiful platform.
          </p>
        </div>
        
        {/* Demo image at the top, larger and centered */}
        <div className="w-full flex justify-center mb-8 px-2 md:px-0">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 md:p-6 flex items-center justify-center">
            <img 
              src="/demo_view.png" 
              alt="Platform Demo Screenshot" 
              className="rounded-xl shadow-lg w-full h-auto object-contain border border-gray-100"
              style={{ background: '#f8fafc' }}
            />
          </div>
        </div>

        {/* Swipable cards and pagination dots */}
        <div className="w-full overflow-hidden px-4 md:px-6 relative">
          {/* Left/right indicator arrows for desktop */}
          <div className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10">
            <button 
              className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200"
              onClick={() => {
                const prevIndex = activeCardIndex === 0 ? features.length - 1 : activeCardIndex - 1;
                scrollToCard(prevIndex);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-green-600">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
          </div>
          
          <div className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10">
            <button 
              className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200"
              onClick={() => {
                const nextIndex = (activeCardIndex + 1) % features.length;
                scrollToCard(nextIndex);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-green-600">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
          
          {/* Swipable cards */}
          <div 
            ref={cardsContainerRef}
            className="flex overflow-x-auto snap-x snap-mandatory pb-6 hide-scrollbar"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {extendedFeatures.map((feature, index) => (
              <div 
                key={`${feature.id}-${index}`}
                className="feature-card snap-center w-[calc(100%-32px)] sm:w-[calc(80%-32px)] md:w-[calc(60%-32px)] lg:w-[calc(40%-32px)] mr-4 flex-shrink-0 transition-all duration-300"
              >
                <div className="bg-white border border-gray-200/50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col min-h-[380px] md:min-h-[420px] overflow-hidden group hover:scale-[1.02]">
                  {/* Card header */}
                  <div className={`p-5 border-b border-gray-100 ${
                    index % 2 === 0 
                      ? 'bg-gradient-to-br from-[#c4ffa0]/10 via-[#c4ffa0]/5 to-transparent' 
                      : 'bg-gradient-to-br from-blue-500/8 via-[#c4ffa0]/5 to-transparent'
                  }`}>
                    <div className="flex items-center mb-2">
                      <div className="bg-[#c4ffa0]/20 rounded-xl p-2 mr-3 shadow-md group-hover:shadow-lg transition-shadow">
                        {React.cloneElement(feature.icon, { className: 'w-5 h-5 text-[#2d4a1e]' })}
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-800">{feature.title}</h3>
                    </div>
                    <p className="text-[#2d4a1e] font-semibold text-sm md:text-base">{feature.subtitle}</p>
                  </div>
                  
                  {/* Enhanced Visual elements */}
                  <div className="p-4 bg-gradient-to-br from-gray-50/50 to-green-50/30 h-28 md:h-32 flex items-center justify-center relative overflow-hidden">
                    {index % features.length === 0 && (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#c4ffa0]/30 to-blue-100/50 rounded-lg"></div>
                        <div className="relative z-10 bg-white rounded-xl p-3 shadow-lg border border-gray-100">
                          <div className="flex items-center space-x-2">
                            <Globe className="w-6 h-6 text-[#2d4a1e]" />
                            <div className="flex flex-col">
                              <div className="h-2 w-16 bg-[#c4ffa0]/30 rounded mb-1"></div>
                              <div className="h-1.5 w-12 bg-gray-200 rounded"></div>
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-2 right-3 bg-[#c4ffa0] text-[#2d4a1e] text-xs px-2 py-1 rounded-full font-medium">
                          SEO ✓
                        </div>
                        <div className="absolute bottom-2 left-3 bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1 text-xs text-gray-600 shadow-sm">
                          Rank #1
                        </div>
                      </div>
                    )}
                    
                    {index % features.length === 1 && (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-100 w-full max-w-[140px]">
                          <div className="flex items-center justify-between mb-2">
                            <Calendar className="w-5 h-5 text-[#2d4a1e]" />
                            <div className="text-xs text-[#2d4a1e] font-medium">BOOK NOW</div>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex justify-between items-center">
                              <div className="h-1.5 w-12 bg-gray-200 rounded"></div>
                              <div className="h-1.5 w-8 bg-[#c4ffa0]/30 rounded"></div>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="h-1.5 w-10 bg-gray-200 rounded"></div>
                              <div className="h-1.5 w-6 bg-blue-300 rounded"></div>
                            </div>
                          </div>
                        </div>
                        <div className="absolute -top-1 -right-1 bg-[#c4ffa0] text-[#2d4a1e] rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                          $
                        </div>
                      </div>
                    )}
                    
                    {index % features.length === 2 && (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-100">
                          <div className="flex items-center mb-2">
                            <Zap className="w-5 h-5 text-[#2d4a1e] mr-2" />
                            <div className="text-xs font-medium text-gray-700">Smart Capture</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-[#c4ffa0]/30 to-[#c4ffa0]/20 rounded-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-[#2d4a1e]" />
                            </div>
                            <div className="text-[#2d4a1e] text-sm font-bold">+47</div>
                          </div>
                        </div>
                        <div className="absolute -bottom-1 -left-1 bg-gradient-to-r from-[#c4ffa0] to-[#c4ffa0]/80 text-[#2d4a1e] rounded-lg px-2 py-1 text-xs font-medium shadow-md">
                          Auto Follow-up
                        </div>
                      </div>
                    )}
                    
                    {index % features.length === 3 && (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 shadow-lg border border-gray-100 w-full max-w-[140px]">
                          <div className="flex items-center justify-between mb-2">
                            <Camera className="w-5 h-5 text-[#2d4a1e]" />
                            <div className="text-xs text-purple-600 font-medium">AI POST</div>
                          </div>
                          <div className="bg-white rounded-lg p-2 mb-2">
                            <div className="h-1 w-full bg-gradient-to-r from-purple-200 to-pink-200 rounded mb-1"></div>
                            <div className="h-1 w-3/4 bg-gray-200 rounded"></div>
                          </div>
                          <div className="flex space-x-1">
                            <div className="w-4 h-4 bg-pink-200 rounded"></div>
                            <div className="w-4 h-4 bg-purple-200 rounded"></div>
                            <div className="w-4 h-4 bg-blue-200 rounded"></div>
                          </div>
                        </div>
                        <div className="absolute top-1 right-1 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          ✨
                        </div>
                      </div>
                    )}
                    
                    {index % features.length === 4 && (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <Link className="w-5 h-5 text-[#2d4a1e]" />
                            <div className="text-xs text-blue-600 font-medium">ONE LINK</div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-[#c4ffa0] rounded-full"></div>
                              <div className="h-1 w-12 bg-[#c4ffa0]/20 rounded"></div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <div className="h-1 w-10 bg-blue-200 rounded"></div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                              <div className="h-1 w-14 bg-purple-200 rounded"></div>
                            </div>
                          </div>
                        </div>
                        <div className="absolute -top-1 -right-1 bg-gradient-to-r from-blue-400 to-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                          🔗
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Card content */}
                  <div className="p-5 flex-grow flex flex-col justify-between">
                    <div>
                      <p className="text-gray-700 mb-3 text-sm font-medium leading-relaxed">
                        {feature.description}
                      </p>
                      
                      <div className="mt-3 space-y-2">
                        {feature.features.slice(0, 4).map((featureItem, i) => (
                          <div key={i} className="flex items-start">
                            <div className="text-green-500 mr-2 mt-0.5 text-sm font-bold">✓</div>
                            <p className="text-gray-600 text-xs md:text-sm leading-relaxed">{featureItem}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Enhanced Pagination dots */}
          <div className="flex justify-center mt-4 gap-3">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  scrollToCard(index);
                  setActiveCardIndex(index);
                }}
                className={`h-3 w-3 rounded-full transition-all duration-300 ${
                  index === activeCardIndex 
                    ? 'bg-[#c4ffa0] shadow-lg scale-110' 
                    : 'bg-[#c4ffa0]/20 hover:bg-[#c4ffa0]/30'
                }`}
                aria-label={`View ${features[index].title} feature`}
              ></button>
            ))}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  );
};

export default SwipableFeatureCardsSection;